import { motion, useScroll, useTransform, AnimatePresence, useReducedMotion } from "framer-motion";
import { useEffect, useRef, useState } from "react";
import { ArrowDown, ShieldCheck, BadgeCheck, CalendarClock } from "lucide-react";
import { TrackCorner, PrimaryButton, GhostButton, EASE } from "./primitives";
import { scrollToId } from "../../lib/smooth";
import { useLanguage } from "../../i18n/LanguageContext";

const Line = ({ children, delay }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className="block"
      initial={{ y: "110%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1.2, delay, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
);

const TELEMETRY_ICONS = [ShieldCheck, BadgeCheck, CalendarClock];

export const Hero = () => {
  const { t } = useLanguage();
  const accents = t.hero.accents || [t.hero.accent];
  const reduceMotion = useReducedMotion();
  const [wordIdx, setWordIdx] = useState(0);
  useEffect(() => {
    if (reduceMotion || accents.length < 2) return;
    const id = setInterval(() => setWordIdx((i) => (i + 1) % accents.length), 3600);
    return () => clearInterval(id);
  }, [reduceMotion, accents.length]);
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const arcsY = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 0.96]);
  const blur = useTransform(scrollYProgress, [0, 0.8], ["blur(0px)", "blur(6px)"]);
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);

  return (
    <section
      id="top"
      ref={ref}
      className="noise relative flex min-h-screen flex-col justify-end overflow-hidden bg-[#021224]"
      data-testid="hero-section"
    >
      {/* cinematic full-bleed backdrop */}
      <motion.div style={{ y: bgY }} className="absolute inset-0">
        <motion.img
          src="/images/hero-stadium-night.jpg"
          alt=""
          fetchPriority="high"
          initial={{ scale: 1.12 }}
          animate={{ scale: 1 }}
          transition={{ duration: 2.4, ease: EASE }}
          className="h-[115%] w-full object-cover"
        />
      </motion.div>
      <div
        className="pointer-events-none absolute inset-0"
        style={{ background: "linear-gradient(180deg, rgba(2,18,36,0.72) 0%, rgba(2,18,36,0.82) 55%, #021224 100%)" }}
      />
      <div className="mesh-grid pointer-events-none absolute inset-0 opacity-60" />
      <div
        className="pointer-events-none absolute inset-0"
        style={{ background: "radial-gradient(circle at 50% 28%, rgba(46,197,230,0.16) 0%, rgba(15,116,196,0.07) 45%, transparent 70%)" }}
      />
      {/* parallax track-corner motif */}
      <motion.div style={{ y: arcsY }} className="pointer-events-none absolute -right-24 -top-24 text-kn-cyan/20">
        <TrackCorner className="h-[420px] w-[420px] rotate-90" />
      </motion.div>
      <motion.div style={{ y: arcsY }} className="pointer-events-none absolute -bottom-32 -left-32 text-white/10">
        <TrackCorner className="h-[380px] w-[380px] -scale-y-100" />
      </motion.div>

      <motion.div
        style={{ opacity: fade, scale, filter: blur }}
        className="relative mx-auto w-full max-w-7xl px-6 pb-24 pt-36 lg:px-10 lg:pt-44"
      >
        <div className="grid items-end gap-16 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1, delay: 0.5 }}
              className="mb-8 flex items-center gap-4 font-mono text-[11px] tracking-[0.32em] text-kn-cyan"
              data-testid="hero-eyebrow"
            >
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-kn-cyan opacity-60" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-kn-cyan" />
              </span>
              {t.hero.eyebrow}
              <span className="h-px w-10 bg-kn-cyan/60" />
            </motion.p>

            <h1
              className="max-w-5xl text-4xl font-bold leading-[1.05] tracking-tight text-white sm:text-6xl lg:text-7xl"
              data-testid="hero-headline"
            >
              <Line delay={0.55}>{t.hero.h1a}</Line>
              <Line delay={0.7}>
                {t.hero.h1b}{" "}
                <span className="relative inline-block overflow-hidden align-bottom">
                  {/* invisible sizer reserves the longest word's width so rotation never reflows the line */}
                  <span className="invisible inline-block whitespace-nowrap font-serif italic font-normal" aria-hidden="true">
                    {accents.reduce((a, b) => (a.length >= b.length ? a : b))}
                  </span>
                  <AnimatePresence mode="wait" initial={false}>
                    <motion.span
                      key={wordIdx}
                      className="absolute left-0 top-0 inline-block whitespace-nowrap font-serif italic font-normal text-brand-gradient"
                      initial={{ y: "110%" }}
                      animate={{ y: "0%" }}
                      exit={{ y: "-110%" }}
                      transition={{ duration: 0.55, ease: EASE }}
                      data-testid="hero-accent-word"
                    >
                      {accents[wordIdx]}
                    </motion.span>
                  </AnimatePresence>
                </span>
              </Line>
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 1.1, ease: EASE }}
              className="mt-8 max-w-xl text-base leading-relaxed text-white/70"
              data-testid="hero-subhead"
            >
              {t.hero.sub}
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 1.3, ease: EASE }}
              className="mt-10 flex flex-wrap items-center gap-4"
            >
              <PrimaryButton testId="hero-join-cta" onClick={() => scrollToId("#join")}>
                {t.hero.ctaPrimary}
              </PrimaryButton>
              <GhostButton testId="hero-orgs-cta" onClick={() => scrollToId("#institutions")}>
                {t.hero.ctaSecondary}
              </GhostButton>
            </motion.div>
          </div>

          {/* floating workforce telemetry */}
          <div className="hidden lg:col-span-5 lg:block">
            <div className="flex flex-col items-end gap-5">
              {t.hero.telemetry.map((c, i) => {
                const Icon = TELEMETRY_ICONS[i];
                return (
                <motion.div
                  key={c.title}
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 1, delay: 1.25 + i * 0.18, ease: EASE }}
                  className={i % 2 === 1 ? "lg:mr-16" : ""}
                  data-testid={`hero-telemetry-${i}`}
                >
                  <motion.div
                    animate={{ y: [0, -8, 0] }}
                    transition={{ duration: 4.5 + i, repeat: Infinity, ease: "easeInOut", delay: i * 0.6 }}
                    className="w-72 rounded-xl border border-kn-cyan/20 bg-kn-navy/70 p-5 shadow-[0_24px_60px_-24px_rgba(2,18,36,0.9)] backdrop-blur-md"
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-mono text-[10px] tracking-[0.24em] uppercase text-kn-cyan">{c.tag}</span>
                      <Icon className="h-4 w-4 text-kn-cyan/70" strokeWidth={1.25} />
                    </div>
                    <p className="mt-3 text-sm font-semibold text-white">{c.title}</p>
                    <div className="mt-4 flex items-center gap-2 border-t border-white/10 pt-3">
                      <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />
                      <span className="font-mono text-[10px] uppercase tracking-wider text-white/55">{c.chip}</span>
                    </div>
                  </motion.div>
                </motion.div>
                );
              })}
            </div>
          </div>
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.9 }}
          className="mt-20 flex items-center gap-3 text-white/40"
          data-testid="hero-scroll-indicator"
        >
          <motion.span animate={{ y: [0, 6, 0] }} transition={{ duration: 2.2, repeat: Infinity, ease: "easeInOut" }}>
            <ArrowDown className="h-4 w-4" strokeWidth={1.5} />
          </motion.span>
          <span className="font-mono text-[10px] tracking-[0.3em] uppercase">{t.hero.scroll}</span>
        </motion.div>
      </motion.div>
    </section>
  );
};
